import { useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import {
    VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES,
    VerificationServiceDetailsReferenceData
} from "@constants/reference-data";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationPolicyConfigDecision } from "@idw-types/data/rp-service-configuration";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { RpServiceConfigurationPolicyDecisionContainer } from "@containers/common/rp-service-configuration-policy-decision-container/rp-service-configuration-policy-decision-container";
import { CardContainer } from "@containers/common/card-container/card-container";
import { DecisionHeaderContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-policies-tab/policy-details-container/decision-engine-policy-editor-container/decision-header-container/decision-header-container";
import { DefaultDecisionContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-policies-tab/policy-details-container/decision-engine-policy-editor-container/default-decision-container/default-decision-container";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { AXNUI_CONTENT_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { getInheritedAndMergedContentPropertyValue } from "@utils/verification-service-details-util";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";

type WorkflowServicePolicyDecisionDetailsModalProps = {
    rpService: FormattedRpServiceConfiguration;
    policyDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision>;
    isDefaultAction: boolean;
    selectedLanguage: LocaleTypes;
    hasUnsubmittedChangeRequest: boolean
    onModalClose: () => void;
};

export const WorkflowServicePolicyDecisionDetailsModal = (props: WorkflowServicePolicyDecisionDetailsModalProps) => {
    const {
        rpService,
        policyDecisions,
        isDefaultAction,
        selectedLanguage,
        hasUnsubmittedChangeRequest,
        onModalClose,
    } = props;

    const referenceData = useReferenceDataSelector<VerificationServiceDetailsReferenceData>(VERIFICATION_SERVICE_DETAILS_REFERENCE_DATA_TYPES, true);

    const resource = resources.modals.workflowServicePolicyDecisionDetails;

    const validationErrors: PolicyDetailErrors = {
        noStepUpApiKeyError: <></>,
        policyConfigDecisionsRetryErrors: {},
        count: 0,
        tabTitle: <></>
    };

    const {
        mergedContent: mergedAXNUIContent
    } = useMemo(() => {
        return getInheritedAndMergedContentPropertyValue(
            rpService.properties,
            rpService.appliedProfiles,
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            DEFAULT_AXNUI_CONTENT,
            selectedLanguage
        );
    }, [
        rpService.properties,
        rpService.appliedProfiles,
        selectedLanguage
    ]);

    const simpleRPServiceWorkflows = useMemo(() => {
        return hasUnsubmittedChangeRequest ? referenceData.simpleAXNAdminRPServiceWorkflows : referenceData.simpleAXNRPServiceWorkflows;
    }, [hasUnsubmittedChangeRequest, referenceData.simpleAXNRPServiceWorkflows, referenceData.simpleAXNAdminRPServiceWorkflows]);

    return (
        <DefaultModal
            automationId={`workflow-service-policy-decision-details-modal`}
            ariaLabelledBy={`workflow-service-policy-decision-details-title`}
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={true}
        >
            <Modal.Header id={`workflow-service-policy-decision-details-modal-title`} closeButton>
                <Modal.Title>{resource.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <>
                    {policyDecisions.map((policyDecision, index) => (
                        <CardContainer key={`policy-config-${policyDecision.policyDecisionType}-decision-${policyDecision.uuid}`}>
                            <DecisionHeaderContainer
                                isDisabled={true}
                                showDragAndDropIndicatorIcon={false}
                                decision={policyDecision}
                                title={`Custom Rule #${index + 1}`}
                                copyDecision={() => { }}
                                copyOppositeDecision={() => { }}
                                removeDecision={() => { }}
                            />

                            <RpServiceConfigurationPolicyDecisionContainer
                                key={`policy-decision-${policyDecision.id}`}
                                isDisabled={true}
                                axnCompanies={referenceData.axnCompanies}
                                axnRpServices={referenceData.axnRPServices}
                                decision={policyDecision}
                                attributes={rpService.attributes}
                                acquiredAttributes={rpService.attributes}
                                policyRetries={rpService.policyConfig.policyRetries}
                                serviceOfferings={rpService.serviceOfferings}
                                axnAttributeTypes={referenceData.axnAttributeTypes}
                                axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                                simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                                unformattedAcquiredAttributes={referenceData.axnAcquiredAttributes}
                                axnAvailableAssertionStatuses={referenceData.axnAvailableAssertionStatuses}
                                rpServiceID={rpService.id}
                                workflowID={rpService.workflowID}
                                validationErrors={validationErrors}
                                setDecision={() => { } } 
                                isUsingThemedUI={false}
                                axnuiContent={mergedAXNUIContent}
                                updateAXNUIContentPolicyDecisionRetryMessage={() => { }}
                                />
                        </CardContainer>
                    ))}
                    {isDefaultAction && (
                        <DefaultDecisionContainer
                            axnRpServices={referenceData.axnRPServices}
                            isUsingThemedUI={true}
                            axnuiContent={mergedAXNUIContent}
                            isDisabled={true}
                            policyConfig={rpService.policyConfig}
                            updatePolicyConfig={() => { }}
                            updateAXNUIContentPropertyValue={() => { }}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            rpServiceID={rpService.id}
                            validationErrors={validationErrors}
                            workflowID={rpService.workflowID}
                        />
                    )}
                </>
            </Modal.Body>
        </DefaultModal>
    );
};